import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Folder, Clock, CheckCircle2, ArrowUpDown, X, Edit2, Trash2, Sparkles, BarChart3, Database, Circle, ChevronRight, ArrowRight, LayoutGrid, List } from 'lucide-react';
import { toast } from 'sonner';
import { useAppStore } from '../stores/appStore';
import { api } from '../lib/api';
import { SESSION_CACHE } from '../lib/queryCache';
import { Case } from '../types';
import Button from '../components/ui/Button';
import Card, { CardHeader, CardBody } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Tooltip from '../components/ui/Tooltip';

// ────────────────────────────────────────────────────────────────────────────
// Phase status helpers — used to make each case row self-explanatory about
// where the case sits in the Discovery → Analysis → Design pipeline.
// ────────────────────────────────────────────────────────────────────────────

type Phase = 'Discovery' | 'Analysis' | 'Design';
type PhaseStatus = 'Not Started' | 'In Progress' | 'Completed';

/** Resolve the per-phase status field for a case's current phase. */
function getCurrentPhaseStatus(caseItem: Case): PhaseStatus {
  const phase = (caseItem.currentPhase as Phase) || 'Discovery';
  if (phase === 'Discovery') return (caseItem.discoveryPhaseStatus || 'In Progress') as PhaseStatus;
  if (phase === 'Analysis') return (caseItem.analysisPhaseStatus || 'In Progress') as PhaseStatus;
  if (phase === 'Design') return (caseItem.designPhaseStatus || 'In Progress') as PhaseStatus;
  return 'In Progress';
}

/** Phase-aware status pill — same shape as the other library pages
 *  (IndustryLibrary, EnterpriseSystemsPage) so the Case Library reads
 *  as part of the same app rather than a distinct design.
 *
 *  Colour by phase, with "Completed" status overriding to emerald
 *  (proven-out signal).  Matches the soft tint + uppercase + small
 *  text pattern used elsewhere. */
function PhaseStatusBadge({ caseItem }: { caseItem: Case }) {
  const phase = (caseItem.currentPhase as Phase) || 'Discovery';
  const status = getCurrentPhaseStatus(caseItem);
  let cls = 'bg-blue-50 text-blue-700 border-blue-200';
  if (status === 'Completed') cls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  else if (phase === 'Analysis') cls = 'bg-amber-50 text-amber-700 border-amber-200';
  else if (phase === 'Design') cls = 'bg-purple-50 text-purple-700 border-purple-200';

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${cls}`}
    >
      {phase} · {status}
    </span>
  );
}

/** Shared rich tooltip body for the whole Case Library tab — a semibold title
 *  over a muted one-line description, with an optional mode chip
 *  ("Editable" / "Read-only").  Every tooltip in this tab routes through this
 *  helper + the themed Tooltip component so they all read as one consistent,
 *  enterprise-grade system instead of a mix of rich tooltips and raw browser
 *  `title` hints. */
function richTip(title: string, desc: string, mode?: 'Editable' | 'Read-only') {
  const chip =
    mode === 'Editable'
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : 'bg-neutral-100 text-neutral-600 border-neutral-200';
  return (
    <div className="text-left">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-neutral-900">{title}</span>
        {mode && (
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${chip}`}>
            {mode}
          </span>
        )}
      </div>
      <div className="text-[11px] text-neutral-500 mt-0.5">{desc}</div>
    </div>
  );
}

/** Back-navigation buttons — let users revisit an earlier phase from a case
 *  row, the way the old Case Library did (regressed when the list was
 *  redesigned in a98808a).  Rules:
 *   - Discovery: nothing earlier → render nothing.
 *   - Analysis:  jump back to Discovery (editable wizard).
 *   - Design:    jump back to Discovery (editable) and Analysis (read-only via
 *                viewMode=true, which skips domain suggestion and shows the
 *                existing analysis without persisting edits).
 *  Each button is wrapped in the themed Tooltip (portal-positioned, arrow,
 *  a11y) for a polished, consistent feel.  Every handler stops propagation so
 *  it doesn't trigger the card/row's own click, which routes to the case's
 *  *current* phase via handleOpenCase. */
function PhaseBackButtons({
  caseItem,
  navigate,
  compact = false,
}: {
  caseItem: Case;
  navigate: (to: string) => void;
  compact?: boolean;
}) {
  const phase = (caseItem.currentPhase as Phase) || 'Discovery';
  if (phase === 'Discovery') return null;

  const id = caseItem.caseId || caseItem.id;
  // Grid (non-compact): stretch each button to fill the row (flex-1 + centered)
  // so the back-nav row lines up edge-to-edge with the full-width Open button
  // below — two buttons split 50/50, a lone button spans the row.  List
  // (compact): keep content-sized inline buttons in the horizontal actions row.
  const size = compact ? 'px-2.5 py-1.5' : 'flex-1 justify-center px-3 py-2';
  const go = (e: React.MouseEvent, to: string) => {
    e.stopPropagation();
    navigate(to);
  };

  return (
    <>
      <Tooltip
        content={richTip('Revisit Discovery', 'Company & industry research', 'Editable')}
        variant="light"
        position="top"
        maxWidth={240}
      >
        <button
          onClick={(e) => go(e, `/cases/new-wizard?caseId=${id}`)}
          aria-label="Revisit Discovery phase"
          className={`flex items-center gap-1.5 ${size} border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 hover:border-blue-300 text-sm font-medium transition-colors`}
        >
          <Sparkles className="w-4 h-4" />
          Discovery
        </button>
      </Tooltip>
      {phase === 'Design' && (
        <Tooltip
          content={richTip('Review Analysis', 'Data mapping & schema design', 'Read-only')}
          variant="light"
          position="top"
          maxWidth={240}
        >
          <button
            onClick={(e) => go(e, `/cases/analysis-wizard?caseId=${id}&viewMode=true`)}
            aria-label="Review Analysis phase (read-only)"
            className={`flex items-center gap-1.5 ${size} border border-amber-200 text-amber-600 rounded-lg hover:bg-amber-50 hover:border-amber-300 text-sm font-medium transition-colors`}
          >
            <BarChart3 className="w-4 h-4" />
            Analysis
          </button>
        </Tooltip>
      )}
    </>
  );
}

/** Relative-time formatter — short form matching IndustryLibrary's age
 *  helper ("1h ago", "May 25"). */
function formatUpdated(iso: string | undefined | null): string {
  if (!iso) return '—';
  try {
    const then = new Date(iso).getTime();
    const diffMs = Math.max(0, Date.now() - then);
    const hrs = diffMs / (1000 * 60 * 60);
    if (hrs < 1) return `${Math.floor(diffMs / 60000)}m ago`;
    if (hrs < 24) return `${Math.floor(hrs)}h ago`;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

type SortOption = 'newest' | 'oldest' | 'name-asc' | 'name-desc';
type FilterOption = 'all' | 'Discovery' | 'Analysis' | 'Design' | 'Draft' | 'Processing' | 'Completed';
type ViewMode = 'grid' | 'list';

// Persist the chosen view mode so it survives reloads/navigation. Each user
// tends to settle on one — power users with many cases want list density,
// occasional users prefer the card grid. Falling back to 'grid' keeps the
// existing default if localStorage is unavailable (private mode, SSR).
const VIEW_MODE_KEY = 'case-library:view-mode';
function readViewMode(): ViewMode {
  try {
    const v = window.localStorage.getItem(VIEW_MODE_KEY);
    return v === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
}

export default function Dashboard() {
  const navigate = useNavigate();
  const setActiveCase = useAppStore((state) => state.setActiveCase);
  const currentUser = useAppStore((state) => state.currentUser);

  // Filter and sort state
  const [statusFilter, setStatusFilter] = useState<FilterOption>('all');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [viewMode, setViewMode] = useState<ViewMode>(() => readViewMode());

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    try {
      window.localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
      // best-effort
    }
  };

  // Delete-confirmation state — replaces the browser's native confirm() dialog
  // with the app's themed ConfirmDialog (same pattern used by IndustryDetail).
  // We track the *case* being deleted (not just the id) so the modal body can
  // surface the case name and the user sees what they're about to remove.
  const [pendingDelete, setPendingDelete] = useState<Case | null>(null);

  // Fetch all cases from Neo4j
  const { data: casesResponse, isLoading } = useQuery<{ cases: Case[]; total: number }>({
    queryKey: ['cases'],
    queryFn: async () => {
      // Fetch all cases from the database
      const response = await api.get<{ cases: Case[]; total: number }>('/cases');
      return response;
    },
    ...SESSION_CACHE,
  });

  // Apply filters and sorting
  const filteredAndSortedCases = useMemo(() => {
    let result = casesResponse?.cases || [];

    // Apply status filter (map phases to statuses)
    if (statusFilter !== 'all') {
      result = result.filter((c) => {
        // Phase-based filtering using currentPhase
        if (statusFilter === 'Discovery') {
          return !c.currentPhase || c.currentPhase === 'Discovery';
        }
        if (statusFilter === 'Analysis') {
          return c.currentPhase === 'Analysis';
        }
        if (statusFilter === 'Design') {
          return c.currentPhase === 'Design';
        }
        // Direct status filtering (for backward compatibility)
        return c.status === statusFilter;
      });
    }

    // Apply sorting
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });

    return result;
  }, [casesResponse?.cases, statusFilter, sortBy]);

  const cases = casesResponse?.cases || [];

  const handleCreateCase = () => {
    navigate('/cases/new');
  };

  const handleOpenCase = (caseItem: Case) => {
    setActiveCase(caseItem);

    // Route based on case phase
    const caseId = caseItem.caseId || caseItem.id;

    // Cases in Design phase go to DesignWizard
    if (caseItem.currentPhase === 'Design') {
      navigate(`/cases/design-wizard?caseId=${caseId}`);
    }
    // Cases in Analysis phase go to AnalysisWizard with auto-analyze flag
    else if (caseItem.currentPhase === 'Analysis') {
      navigate(`/cases/analysis-wizard?caseId=${caseId}&autoAnalyze=true`);
    }
    // Cases in Discovery phase (or no phase) go to NewCaseWizard
    else {
      navigate(`/cases/new-wizard?caseId=${caseId}`);
    }
  };

  const handleEditCase = (e: React.MouseEvent, caseItem: Case) => {
    e.stopPropagation(); // Prevent row click
    const caseId = caseItem.caseId || caseItem.id;
    navigate(`/cases/new-wizard?caseId=${caseId}`);
  };

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (caseId: string) =>
      api.delete(`/cases/${caseId}/soft`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      toast.success('Case deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to delete case');
    },
  });

  const handleDeleteCase = (e: React.MouseEvent, caseItem: Case) => {
    e.stopPropagation(); // Prevent row click
    // Open themed ConfirmDialog instead of window.confirm() — the actual
    // mutation fires from onConfirm in the dialog JSX below.
    setPendingDelete(caseItem);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const caseId = pendingDelete.caseId || pendingDelete.id;
    if (!caseId) return;
    try {
      await deleteMutation.mutateAsync(caseId);
    } finally {
      setPendingDelete(null);
    }
  };

  const handleFilterClick = (filter: FilterOption) => {
    setStatusFilter(filter === statusFilter ? 'all' : filter);
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
  };

  const getListHeading = () => {
    switch (statusFilter) {
      case 'Discovery':
        return 'Discovery Cases';
      case 'Analysis':
        return 'Analysis Cases';
      case 'Design':
        return 'Design Cases';
      case 'Draft':
        return 'Draft Cases';
      case 'Processing':
        return 'Cases in Progress';
      case 'Completed':
        return 'Completed Cases';
      default:
        return 'Recent Cases';
    }
  };

  const getStatusIcon = (status: Case['status']) => {
    switch (status) {
      case 'Processing':
        return <Clock className="w-5 h-5" />;
      case 'Completed':
        return <CheckCircle2 className="w-5 h-5" />;
      case 'Draft':
        return <Folder className="w-5 h-5" />;
      default:
        return <Folder className="w-5 h-5" />;
    }
  };

  const getStatusVariant = (status: Case['status']): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
    switch (status) {
      case 'Draft':
        return 'secondary';
      case 'Processing':
        return 'warning';
      case 'Completed':
        return 'success';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-[40px] font-bold text-neutral-900 mb-2">Case Library</h1>
          <p className="text-neutral-600">
            Manage your Business Transformation Studio cases
          </p>
        </div>
        <Button onClick={handleCreateCase} size="lg">
          <Plus className="w-5 h-5" />
          New Case
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        {/* Total Cases - Non-clickable */}
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Folder className="w-6 h-6 text-primary-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-neutral-600 mb-1">Total Cases</p>
              <p className="text-2xl font-bold text-neutral-900">
                {cases.length}
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                All your cases
              </p>
            </div>
          </CardBody>
        </Card>

        {/* Discovery - Clickable */}
        <button
          onClick={() => handleFilterClick('Discovery')}
          className={`text-left transition-all ${
            statusFilter === 'Discovery'
              ? 'ring-2 ring-blue-500 ring-offset-2'
              : 'hover:shadow-md'
          }`}
        >
          <Card>
            <CardBody className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-neutral-600 mb-1">Discovery</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {cases.filter((p) => !p.currentPhase || p.currentPhase === 'Discovery').length}
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  Company & Industry Research
                </p>
              </div>
            </CardBody>
          </Card>
        </button>

        {/* Analysis - Clickable */}
        <button
          onClick={() => handleFilterClick('Analysis')}
          className={`text-left transition-all ${
            statusFilter === 'Analysis'
              ? 'ring-2 ring-amber-500 ring-offset-2'
              : 'hover:shadow-md'
          }`}
        >
          <Card>
            <CardBody className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-neutral-600 mb-1">Analysis</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {cases.filter((p) => p.currentPhase === 'Analysis').length}
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  Data Mapping & Schema Design
                </p>
              </div>
            </CardBody>
          </Card>
        </button>

        {/* Design - Clickable */}
        <button
          onClick={() => handleFilterClick('Design')}
          className={`text-left transition-all ${
            statusFilter === 'Design'
              ? 'ring-2 ring-green-500 ring-offset-2'
              : 'hover:shadow-md'
          }`}
        >
          <Card>
            <CardBody className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-neutral-600 mb-1">Design</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {cases.filter((p) => p.currentPhase === 'Design').length}
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  Insights & Intelligence Delivery
                </p>
              </div>
            </CardBody>
          </Card>
        </button>
      </div>

      {/* Projects List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-bold text-neutral-900">{getListHeading()}</h2>
              {statusFilter !== 'all' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                >
                  <X className="w-4 h-4" />
                  Clear Filter
                </Button>
              )}
            </div>
            {/* View toggle + Sort */}
            <div className="flex items-center gap-3">
              {/* Grid / List view toggle — segmented control */}
              <div
                className="inline-flex items-center border border-neutral-300 rounded-lg overflow-hidden"
                role="group"
                aria-label="View mode"
              >
                <Tooltip content={richTip('Grid view', 'Cards in a responsive grid')} variant="light">
                  <button
                    onClick={() => handleViewModeChange('grid')}
                    aria-pressed={viewMode === 'grid'}
                    aria-label="Grid view"
                    className={`px-2.5 py-2 text-sm flex items-center transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-neutral-600 hover:bg-neutral-50'
                    }`}
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                </Tooltip>
                <Tooltip content={richTip('List view', 'Compact rows for scanning')} variant="light">
                  <button
                    onClick={() => handleViewModeChange('list')}
                    aria-pressed={viewMode === 'list'}
                    aria-label="List view"
                    className={`px-2.5 py-2 text-sm flex items-center transition-colors border-l border-neutral-300 ${
                      viewMode === 'list'
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-neutral-600 hover:bg-neutral-50'
                    }`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </Tooltip>
              </div>

              {/* Sort Dropdown */}
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-neutral-600" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="px-3 py-2 bg-white border border-neutral-300 rounded-lg text-sm text-neutral-900 hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent transition-all"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                </select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-8">
              <Spinner size="lg" />
            </div>
          ) : filteredAndSortedCases.length > 0 ? (
            viewMode === 'grid' ? (
            /* Mirrors IndustryLibrary's card-grid: 1/2/3 col responsive,
               flat `bg-white border` cards with subtle hover-shadow, no
               accent bars or progress bars.  Each card has the standard
               header (title + status pill) + 2x2 stats grid + flat
               actions row — same shape as Industry / Enterprise Systems
               libraries so the Case Library reads as part of the same
               app, not a separate design language. */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {filteredAndSortedCases.map((caseItem) => {
                const phase = (caseItem.currentPhase as Phase) || 'Discovery';
                const status = getCurrentPhaseStatus(caseItem);
                return (
                  <div
                    key={caseItem.caseId || caseItem.id}
                    className="bg-white border border-neutral-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => handleOpenCase(caseItem)}
                  >
                    {/* Header — title + status pill (top right) */}
                    <div className="flex items-start justify-between mb-4 gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-neutral-900 mb-1 line-clamp-2">
                          {caseItem.name}
                        </h3>
                        {caseItem.description && (
                          <p className="text-sm text-neutral-600 line-clamp-2">
                            {caseItem.description}
                          </p>
                        )}
                      </div>
                      <PhaseStatusBadge caseItem={caseItem} />
                    </div>

                    {/* Stats grid (2x2) — Phase / Status / Updated / Created.
                        Matches the IndustryLibrary metrics block exactly:
                        small label + bold value, border-top separator. */}
                    <div className="grid grid-cols-2 gap-4 mb-4 pt-4 border-t border-neutral-200">
                      <div>
                        <div className="text-xs text-neutral-600">Phase</div>
                        <div className="text-sm font-semibold text-neutral-900">{phase}</div>
                      </div>
                      <div>
                        <div className="text-xs text-neutral-600">Status</div>
                        <div className="text-sm font-semibold text-neutral-900">{status}</div>
                      </div>
                      <div>
                        <div className="text-xs text-neutral-600">Updated</div>
                        <div className="text-sm font-semibold text-neutral-900">
                          {formatUpdated(caseItem.updatedAt || caseItem.createdAt)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-neutral-600">Created</div>
                        <div className="text-sm font-semibold text-neutral-900">
                          {new Date(caseItem.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Revisit earlier phases — only renders for Analysis /
                        Design cases (returns null for Discovery), so Discovery
                        cards stay visually unchanged. */}
                    {phase !== 'Discovery' && (
                      <div className="flex gap-2 mb-2">
                        <PhaseBackButtons caseItem={caseItem} navigate={navigate} />
                      </div>
                    )}

                    {/* Actions — primary "Open" (flex-1) + icon-only delete.
                        Same shape as IndustryLibrary's actions row. */}
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenCase(caseItem);
                        }}
                        className="flex-1 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
                      >
                        Open
                      </button>
                      {caseItem.status === 'Processing' && (
                        <Tooltip content={richTip('Edit details', 'Update case name & description')} variant="light">
                          <button
                            onClick={(e) => handleEditCase(e, caseItem)}
                            aria-label={`Edit ${caseItem.name}`}
                            className="px-3 py-2 border border-neutral-200 text-neutral-600 rounded-lg hover:bg-neutral-50 hover:border-neutral-300 text-sm font-medium"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      )}
                      <Tooltip content={richTip('Delete case', 'Archive — restorable later')} variant="light">
                        <button
                          onClick={(e) => handleDeleteCase(e, caseItem)}
                          aria-label={`Delete ${caseItem.name}`}
                          className="px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 hover:border-red-300 text-sm font-medium"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </div>
            ) : (
              /* List view — compact rows for scanning many cases at a
                 glance.  Same visual language (neutral borders, status
                 pill, primary "Open" button) as the grid view, just
                 horizontal: title + description on the left, phase pill
                 + updated time + actions on the right. */
              <div className="divide-y divide-neutral-200">
                {filteredAndSortedCases.map((caseItem) => {
                  return (
                    <div
                      key={caseItem.caseId || caseItem.id}
                      className="flex items-center gap-4 px-6 py-4 hover:bg-neutral-50 transition-colors cursor-pointer"
                      onClick={() => handleOpenCase(caseItem)}
                    >
                      {/* Title + description */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-neutral-900 truncate mb-0.5">
                          {caseItem.name}
                        </h3>
                        {caseItem.description && (
                          <p className="text-xs text-neutral-600 truncate">
                            {caseItem.description}
                          </p>
                        )}
                      </div>

                      {/* Phase · Status pill — sole carrier of phase/status
                          info; the previous design also had separate Phase
                          and Status text columns which restated the same
                          values, just noisier. */}
                      <div className="hidden md:block flex-shrink-0">
                        <PhaseStatusBadge caseItem={caseItem} />
                      </div>

                      {/* Updated — relative time, right-aligned so the eye
                          travels predictably across rows. */}
                      <div className="hidden sm:block flex-shrink-0 w-20 text-right">
                        <div className="text-xs text-neutral-500">Updated</div>
                        <div className="text-sm font-medium text-neutral-900">
                          {formatUpdated(caseItem.updatedAt || caseItem.createdAt)}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <PhaseBackButtons caseItem={caseItem} navigate={navigate} compact />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenCase(caseItem);
                          }}
                          className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
                        >
                          Open
                        </button>
                        {caseItem.status === 'Processing' && (
                          <Tooltip content={richTip('Edit details', 'Update case name & description')} variant="light">
                            <button
                              onClick={(e) => handleEditCase(e, caseItem)}
                              aria-label={`Edit ${caseItem.name}`}
                              className="px-2.5 py-1.5 border border-neutral-200 text-neutral-600 rounded-lg hover:bg-neutral-50 hover:border-neutral-300 text-sm font-medium"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        )}
                        <Tooltip content={richTip('Delete case', 'Archive — restorable later')} variant="light">
                          <button
                            onClick={(e) => handleDeleteCase(e, caseItem)}
                            aria-label={`Delete ${caseItem.name}`}
                            className="px-2.5 py-1.5 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 hover:border-red-300 text-sm font-medium"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <div className="p-12 text-center">
              <Folder className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                {statusFilter !== 'all' ? `No ${statusFilter.toLowerCase()} cases` : 'No cases yet'}
              </h3>
              <p className="text-neutral-600 mb-6">
                {statusFilter !== 'all'
                  ? 'Try changing the filter or create a new case'
                  : 'Create your first case to get started with Amplinth'}
              </p>
              {statusFilter !== 'all' ? (
                <Button variant="outline" onClick={handleClearFilters}>
                  <X className="w-4 h-4" />
                  Clear Filter
                </Button>
              ) : (
                <Button onClick={handleCreateCase}>
                  <Plus className="w-4 h-4" />
                  Create Case
                </Button>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Themed confirm dialog for case deletion. Replaces the browser's
          native window.confirm() so the destructive-action UX matches the
          rest of the app and we get a spinner on the confirm button while
          the soft-delete mutation is in flight. The success/error toast is
          already wired in deleteMutation's onSuccess / onError handlers. */}
      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title={`Delete "${pendingDelete?.name ?? 'case'}"?`}
        message={[
          'This case will be archived (soft-deleted).',
          'An administrator can restore it later if needed.',
        ]}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteMutation.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
